import { Link } from "react-router-dom";
import { ArrowRight, MapPin } from "lucide-react";
import { IMG, BUSH_MAPS_USES } from "../data/content";
import Seo from "../components/Seo";

export default function BushMaps() {
  return (
    <div data-testid="page-bush-maps">
      <Seo
        title="Bush Maps — Ecological mapping for seed, restoration and Country"
        description="Bush Maps help Indigenous communities identify and communicate seed collection zones, cultural plant species, ecosystems, restoration priorities and ranger activities."
        path="/bush-maps"
      />

      {/* Hero */}
      <section className="border-b border-rule">
        <div className="max-w-7xl mx-auto px-6 md:px-10 py-20 md:py-28 grid grid-cols-1 lg:grid-cols-12 gap-12 items-start">
          <div className="lg:col-span-7">
            <div className="flex items-center gap-3 mb-6">
              <span className="stamp">Bush Maps</span>
              <span className="specimen-label">Ecological Mapping</span>
            </div>
            <h1 className="font-serif text-4xl sm:text-5xl md:text-6xl lg:text-7xl leading-[0.98]">
              Every seed tells a story <em className="text-eucalypt">about a landscape.</em>
            </h1>
            <p className="mt-10 text-base md:text-lg leading-relaxed text-ink/85 max-w-2xl">
              Bush Maps help Indigenous communities identify and communicate the species, sites,
              cultural landscapes and restoration priorities that sit within their Country.
            </p>
            <p className="mt-4 text-base md:text-lg leading-relaxed text-ink/85 max-w-2xl">
              By combining ecological knowledge, cultural information and spatial tools, communities
              can make more informed decisions about how seed is collected, stored, and returned to
              Country — and tell the story of that Country to the partners they choose to share it
              with.{" "}
              <a
                href="https://www.bushmaps.com.au"
                target="_blank"
                rel="noopener noreferrer"
                data-testid="bush-maps-external-link"
                className="link-mono text-eucalypt inline whitespace-nowrap"
              >
                www.bushmaps.com.au
              </a>
            </p>
          </div>
          <div className="lg:col-span-5">
            <div className="polaroid">
              <img
                src={IMG.ngaanyatjarraBushFoods}
                alt="Bush Foods & Botanicals of the Ngaanyatjarra Lands — illustrated knowledge chart"
                className="w-full h-auto block bg-cream"
              />
            </div>
            <p className="mt-3 text-xs text-muted italic">
              Mapping seed collection — turning Country into shared, governed knowledge.
            </p>
          </div>
        </div>
      </section>

      {/* What Bush Maps surface */}
      <section className="border-b border-rule bg-parchment/40">
        <div className="max-w-7xl mx-auto px-6 md:px-10 py-20 md:py-28">
          <p className="section-num mb-3">What Bush Maps Surface</p>
          <h2 className="font-serif text-3xl sm:text-4xl md:text-5xl lg:text-6xl leading-[1.02] max-w-4xl mb-14">
            Layers of knowledge — held together under cultural authority.
          </h2>
          <div
            className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-px bg-rule border border-rule"
            data-testid="bush-maps-uses"
          >
            {BUSH_MAPS_USES.map((u, i) => (
              <div
                key={u}
                className="bg-card p-6 md:p-7 min-h-[150px] flex flex-col justify-between"
                data-testid={`bush-maps-use-${i}`}
              >
                <span className="specimen-label">{String(i + 1).padStart(2, "0")}</span>
                <p className="font-serif text-xl md:text-2xl leading-tight">{u}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* What they're used for */}
      <section className="border-b border-rule">
        <div className="max-w-6xl mx-auto px-6 md:px-10 py-20 md:py-24 grid grid-cols-1 lg:grid-cols-12 gap-10 items-start">
          <div className="lg:col-span-5">
            <img
              src={IMG.collectionApp}
              alt="Field collection app — recording data on Country"
              className="w-full h-[460px] object-cover border border-rule p-2 bg-cream"
            />
          </div>
          <div className="lg:col-span-7">
            <p className="section-num mb-3">How Communities Use Bush Maps</p>
            <h2 className="font-serif text-3xl sm:text-4xl md:text-5xl leading-tight max-w-3xl">
              Planning, education, funding and engagement — built on the community&rsquo;s own knowledge.
            </h2>
            <ul
              className="mt-10 grid grid-cols-1 sm:grid-cols-2 gap-px bg-rule border border-rule"
              data-testid="bush-maps-purposes"
            >
              {[
                ["Planning", "Seed collection, restoration and ranger activity calendars."],
                ["Education", "Schools, on-Country trips and intergenerational knowledge transfer."],
                ["Funding applications", "Grounded, spatial evidence for grants and investment."],
                ["Community engagement", "Telling the story of Country in the community's own terms."],
              ].map(([k, v], i) => (
                <li
                  key={k}
                  className="bg-card p-5 md:p-6 flex gap-4 items-start"
                  data-testid={`bush-maps-purpose-${i}`}
                >
                  <MapPin size={16} className="text-ochre2 mt-1.5 flex-shrink-0" />
                  <div>
                    <p className="font-serif text-lg md:text-xl leading-snug">{k}</p>
                    <p className="text-xs text-muted mt-1 leading-relaxed">{v}</p>
                  </div>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </section>

      {/* Governance principle */}
      <section className="border-b border-rule bg-bark text-chalk">
        <div className="max-w-5xl mx-auto px-6 md:px-10 py-20 md:py-24">
          <p className="specimen-label text-chalk/60 mb-6">Cultural Authority</p>
          <p className="font-serif text-2xl sm:text-3xl md:text-4xl lg:text-5xl leading-[1.15] text-chalk">
            Bush Maps remain under the control of Traditional Owners. <em className="text-ochre">Decisions about what is mapped, shared and restricted rest with the community whose Country it is.</em>
          </p>
          <div className="mt-10">
            <Link
              to="/seed-intelligence"
              data-testid="bush-maps-governance-link"
              className="link-mono text-ochre inline-flex items-center gap-2"
            >
              Read our position on data governance <ArrowRight size={14} />
            </Link>
          </div>
        </div>
      </section>

      {/* Closing CTA */}
      <section>
        <div className="max-w-4xl mx-auto px-6 md:px-10 py-24 text-center">
          <p className="section-num mb-8">Bush Maps</p>
          <h2 className="font-serif text-2xl sm:text-3xl md:text-4xl lg:text-5xl leading-[1.15]">
            If your community is mapping Country, <em className="text-eucalypt">we&rsquo;d like to support that work.</em>
          </h2>
          <div className="mt-12">
            <Link
              to="/contact"
              data-testid="bush-maps-cta"
              className="inline-flex items-center gap-3 bg-eucalypt text-chalk px-8 py-4 font-mono text-[11px] tracking-[0.25em] uppercase hover:bg-eucalypt-dark rounded-full hover:-translate-y-0.5 transition-all"
            >
              Talk to us about Bush Maps <ArrowRight size={14} />
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
